import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronRight, Star, Shield, Clock, Award, Sparkles, Send, MessageCircle, Phone } from "lucide-react";
import * as LucideIcons from "lucide-react";
import { useApp, t } from "@/lib/AppContext";
import { api, resolveImg } from "@/lib/api";
import CarCard from "@/components/CarCard";
import SectionLabel from "@/components/SectionLabel";
import FeedbackDialog from "@/components/FeedbackDialog";

const ICON_FALLBACK = LucideIcons.Wrench;

export default function Home() {
  const { content, settings } = useApp();
  const [cars, setCars] = useState([]);
  const [services, setServices] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  useEffect(() => {
    api.get("/cars", { params: { is_featured: true, per_page: 6 } }).then(r => setCars(r.data.items || [])).catch(() => {});
    api.get("/services").then(r => setServices(r.data || [])).catch(() => {});
    api.get("/reviews").then(r => setReviews(r.data || [])).catch(() => {});
  }, []);

  const heroBg = settings?.bg_image_urls?.hero;
  const howBg = settings?.bg_image_urls?.how_we_work;
  const servicesBg = settings?.bg_image_urls?.services;
  const steps = t(content, "how_we_work.steps", []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="hero-section" data-testid="hero-section">
        {heroBg && <div className="hero-bg" style={{ backgroundImage: `url(${heroBg})` }} />}
        <div className="hero-overlay" />
        <div className="hero-content">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
            <div className="max-w-3xl">
              <SectionLabel light>{t(content, "hero.label", "")}</SectionLabel>
              <h1 className="hero-title mt-5" data-testid="hero-title">{t(content, "hero.title", "")}</h1>
              <p className="hero-subtitle mt-5">{t(content, "hero.subtitle", "")}</p>
              <div className="flex flex-col sm:flex-row gap-3 mt-8">
                <Link to="/catalog" className="btn-primary-cta" data-testid="hero-cta-catalog">
                  {t(content, "hero.btn_primary", "Смотреть каталог")} <ArrowRight size={16} />
                </Link>
                <Link to="/configurator" className="btn-outline-light" data-testid="hero-cta-configurator">
                  {t(content, "hero.btn_secondary", "Подобрать авто")}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* STATS STRIP */}
      <section className="py-12 md:py-16 bg-white border-b" style={{ borderColor: "var(--color-border)" }} data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-4">
            {[
              { num: t(content, "stats.stat1_num", "7+"), label: t(content, "stats.stat1_label", "лет на рынке") },
              { num: t(content, "stats.stat2_num", "300+"), label: t(content, "stats.stat2_label", "авто доставлено") },
              { num: t(content, "stats.stat3_num", "98%"), label: t(content, "stats.stat3_label", "довольных клиентов") },
            ].map((s, i) => (
              <div key={i} className="text-center md:text-left flex md:block items-baseline gap-4 md:border-l-4 md:pl-6" style={{ borderColor: i === 0 ? "transparent" : "var(--color-border)" }} data-testid={`stat-${i}`}>
                <div className="stat-number">{s.num}</div>
                <div className="font-accent text-sm uppercase tracking-wider text-[var(--color-text-secondary)] mt-2">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CATALOG */}
      <section className="py-16 md:py-24 bg-[var(--color-bg-primary)]" data-testid="catalog-preview">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
            <div>
              <SectionLabel>{t(content, "catalog.label", "")}</SectionLabel>
              <h2 className="section-heading mt-4">{t(content, "catalog.title", "")}</h2>
              <p className="text-[var(--color-text-secondary)] mt-3 max-w-xl">{t(content, "catalog.subtitle", "")}</p>
            </div>
            <Link to="/catalog" className="hidden md:inline-flex items-center gap-1 font-accent text-sm uppercase tracking-wider font-semibold text-[var(--color-secondary)] hover:text-[var(--color-primary)]">
              {t(content, "catalog.btn_view_all", "Весь каталог")} <ChevronRight size={16} />
            </Link>
          </div>

          {cars.length === 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1,2,3,4,5,6].map((i) => <div key={i} className="skeleton h-72" />)}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {cars.map((c) => <CarCard key={c.id} car={c} />)}
            </div>
          )}

          <div className="text-center mt-10 md:hidden">
            <Link to="/catalog" className="btn-secondary-cta">
              {t(content, "catalog.btn_view_all", "Весь каталог")} <ChevronRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* HOW WE WORK */}
      <section className="relative py-16 md:py-24 section-on-dark" data-testid="how-we-work">
        <div className="absolute inset-0">
          {howBg && <img src={resolveImg(howBg)} alt="" className="w-full h-full object-cover" />}
          <div className="absolute inset-0" style={{ background: "rgba(15,36,68,0.92)" }} />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <SectionLabel light>{t(content, "how_we_work.label", "")}</SectionLabel>
            <h2 className="section-heading text-white mt-4">{t(content, "how_we_work.title", "")}</h2>
            <p className="text-white/70 mt-3">{t(content, "how_we_work.subtitle", "")}</p>
          </div>

          <div className="relative">
            {/* Red connector line on desktop */}
            <div className="hidden lg:block absolute top-[30px] left-0 right-0 h-0.5" style={{ background: "var(--color-primary)", opacity: 0.4 }} />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-8 relative">
              {(steps || []).map((step, i) => (
                <div key={i} className="text-center" data-testid={`step-${i}`}>
                  <div className="step-circle mx-auto mb-4">{i + 1}</div>
                  <h3 className="font-headings font-bold text-white text-lg mb-2">{step.title}</h3>
                  <p className="text-white/65 text-sm">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="relative py-16 md:py-24" data-testid="services-section">
        <div className="absolute inset-0">
          {servicesBg && <img src={resolveImg(servicesBg)} alt="" className="w-full h-full object-cover" />}
          <div className="absolute inset-0" style={{ background: "rgba(28,58,107,0.88)" }} />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <SectionLabel light>{t(content, "services.label", "")}</SectionLabel>
            <h2 className="section-heading text-white mt-4">{t(content, "services.title", "")}</h2>
            <p className="text-white/70 mt-3">{t(content, "services.subtitle", "")}</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {services.map((s) => {
              const Icon = LucideIcons[s.icon] || ICON_FALLBACK;
              return (
                <div key={s.id} className="bg-white/5 backdrop-blur-sm rounded-[var(--radius-lg)] p-6 border border-white/10 hover:border-[var(--color-gold)]/60 transition-colors" data-testid={`service-${s.id}`}>
                  <div className="w-12 h-12 rounded-md flex items-center justify-center mb-4" style={{ background: "var(--color-primary)" }}>
                    <Icon size={22} className="text-white" />
                  </div>
                  <h3 className="font-headings font-bold text-white text-lg mb-2">{s.title}</h3>
                  <p className="text-white/70 text-sm leading-relaxed">{s.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="py-16 md:py-24 bg-white" data-testid="why-us">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-12">
            <SectionLabel>{t(content, "why_us.label", "Преимущества")}</SectionLabel>
            <h2 className="section-heading mt-4">{t(content, "why_us.title", "Почему выбирают нас")}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[Sparkles, Shield, Clock, Award].map((Icon, i) => {
              const items = t(content, "why_us.items", []) || [];
              const it = items[i] || {};
              return (
                <div key={i} className="p-6 rounded-[var(--radius-lg)] border" style={{ borderColor: "var(--color-border)" }} data-testid={`why-us-${i}`}>
                  <div className="w-12 h-12 rounded-md flex items-center justify-center mb-4" style={{ background: "rgba(204,0,0,0.1)" }}>
                    <Icon size={22} style={{ color: "var(--color-primary)" }} />
                  </div>
                  <h3 className="font-headings font-bold text-base mb-2">{it.title}</h3>
                  <p className="text-sm text-[var(--color-text-secondary)] leading-relaxed">{it.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* REVIEWS */}
      <section className="py-16 md:py-24 bg-[var(--color-bg-primary)]" data-testid="reviews-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <SectionLabel>{t(content, "reviews.label", "")}</SectionLabel>
            <h2 className="section-heading mt-4">{t(content, "reviews.title", "")}</h2>
            <p className="text-[var(--color-text-secondary)] mt-3">{t(content, "reviews.subtitle", "")}</p>
          </div>
          {reviews.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-[var(--color-text-secondary)]">Скоро здесь появятся отзывы наших клиентов</div>
              <Link to="/reviews" className="btn-secondary-cta mt-6 inline-flex">Оставить отзыв</Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {reviews.slice(0, 6).map((rv) => (
                <div key={rv.id} className="car-card p-6" data-testid={`review-${rv.id}`} style={{ borderTop: "3px solid var(--color-gold)" }}>
                  <div className="flex gap-1 mb-3">
                    {[1,2,3,4,5].map((i) => (
                      <Star key={i} size={16} fill={i <= rv.rating ? "var(--color-gold)" : "transparent"} stroke="var(--color-gold)" />
                    ))}
                  </div>
                  <p className="text-[var(--color-text-primary)] mb-4 leading-relaxed">{rv.text}</p>
                  <div className="text-sm">
                    <div className="font-headings font-bold">{rv.author}</div>
                    {rv.car_name && <div className="text-[var(--color-text-secondary)] text-xs mt-1">{rv.car_name}</div>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-16 md:py-20 overflow-hidden" style={{ background: "var(--color-bg-dark)" }} data-testid="cta-section">
        <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: "radial-gradient(circle at 20% 50%, var(--color-primary) 0%, transparent 50%), radial-gradient(circle at 80% 50%, var(--color-gold) 0%, transparent 50%)" }} />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <SectionLabel light>{t(content, "cta_block.label", "Готовы начать?")}</SectionLabel>
          <h2 className="font-headings font-extrabold text-white text-3xl md:text-5xl mt-4">{t(content, "cta_block.title", "Готовы к покупке?")}</h2>
          <p className="text-white/80 mt-4 max-w-2xl mx-auto">{t(content, "cta_block.subtitle", "")}</p>
          <button onClick={() => setFeedbackOpen(true)} className="mt-8 btn-primary-cta" data-testid="cta-button">
            {t(content, "cta_block.btn_text", "Оставить заявку")} <ArrowRight size={16} />
          </button>
        </div>
      </section>

      {/* CONTACTS */}
      <section className="py-16 md:py-24 bg-white" data-testid="contacts-preview">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <SectionLabel>{t(content, "contacts.label", "")}</SectionLabel>
              <h2 className="section-heading mt-4">{t(content, "contacts.title", "")}</h2>
              <p className="text-[var(--color-text-secondary)] mt-3">{t(content, "contacts.subtitle", "")}</p>
              <div className="mt-6 space-y-3">
                {settings?.phone && (
                  <a href={`tel:${settings.phone.replace(/[^+\d]/g, "")}`} className="flex items-center gap-3 text-lg font-accent font-semibold hover:text-[var(--color-primary)]">
                    <Phone size={20} style={{ color: "var(--color-primary)" }} /> {settings.phone}
                  </a>
                )}
              </div>
            </div>
            <div className="space-y-3">
              {settings?.telegram_url && (
                <a href={settings.telegram_url} target="_blank" rel="noopener noreferrer" className="flex items-center justify-between gap-3 p-5 rounded-[var(--radius-lg)] text-white" style={{ background: "#229ED9" }} data-testid="home-tg-btn">
                  <span className="flex items-center gap-3"><Send size={20} /><span className="font-accent font-semibold uppercase tracking-wider text-sm">{t(content, "contacts.btn_telegram", "Написать в Telegram")}</span></span>
                  <ChevronRight size={18} />
                </a>
              )}
              {settings?.whatsapp_url && (
                <a href={settings.whatsapp_url} target="_blank" rel="noopener noreferrer" className="flex items-center justify-between gap-3 p-5 rounded-[var(--radius-lg)] text-white" style={{ background: "#25D366" }} data-testid="home-wa-btn">
                  <span className="flex items-center gap-3"><MessageCircle size={20} /><span className="font-accent font-semibold uppercase tracking-wider text-sm">{t(content, "contacts.btn_whatsapp", "Написать в WhatsApp")}</span></span>
                  <ChevronRight size={18} />
                </a>
              )}
              <button onClick={() => setFeedbackOpen(true)} className="flex items-center justify-between gap-3 p-5 rounded-[var(--radius-lg)] w-full text-white" style={{ background: "var(--color-secondary)" }} data-testid="home-form-btn">
                <span className="font-accent font-semibold uppercase tracking-wider text-sm">{t(content, "feedback.contact_title", "Написать менеджеру")}</span>
                <ChevronRight size={18} />
              </button>
            </div>
          </div>
        </div>
      </section>

      <FeedbackDialog open={feedbackOpen} onOpenChange={setFeedbackOpen} />
    </div>
  );
}
